import { useRef, useState } from 'react';
import type { ChangeEvent, JSX } from 'react';
import { CameraIcon, PaperclipIcon, RecordIcon } from '../../../_components/Icons.js';
import type { Attachment, CaptureBusyState, FeedbackPalette } from '../types.js';
import { AttachmentList } from './AttachmentList.js';
import { CaptureButton } from './CaptureButton.js';

interface AttachmentBarProps {
  palette: FeedbackPalette;
  attachments: Attachment[];
  onAdd: (attachment: Attachment) => void;
  onRemove: (index: number) => void;
  onRequestScreenshot: () => Promise<void>;
  onRequestRecording: () => Promise<void>;
  busy: CaptureBusyState;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export function AttachmentBar({
  palette,
  attachments,
  onAdd,
  onRemove,
  onRequestScreenshot,
  onRequestRecording,
  busy,
}: AttachmentBarProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('File too large (max 10 MB).');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      onAdd({ url: reader.result as string, kind: 'file', filename: file.name });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        <CaptureButton
          palette={palette}
          label="Screenshot"
          busy={busy === 'screenshot'}
          onClick={onRequestScreenshot}
          icon={<CameraIcon />}
        />
        <CaptureButton
          palette={palette}
          label="Record"
          busy={busy === 'recording'}
          onClick={onRequestRecording}
          icon={<RecordIcon />}
        />
        <CaptureButton
          palette={palette}
          label="File"
          busy={busy === 'file'}
          onClick={() => fileInputRef.current?.click()}
          icon={<PaperclipIcon />}
        />
        <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
      </div>
      {error && <p style={{ color: '#dc2626', fontSize: 12, margin: '8px 0 0' }}>{error}</p>}
      <AttachmentList palette={palette} attachments={attachments} onRemove={onRemove} />
    </div>
  );
}
